import { oc } from "@orpc/contract";
import {
  MetadataChangeSetEventAction,
  MetadataChangeSetStatus,
  MetadataChangeSetVersionSource,
} from "@erp/shared-types";
import { z } from "zod";
import {
  As400BranchCodeSchema,
  As400CellValueSchema,
  As400ExportGroupSchema,
  As400ExportRowSchema,
  As400ExportRowSourceSchema,
  CatalogAdvancedFilterSchema,
  CatalogRecordKeyScopeSchema,
  catalogFilterMatchField,
  ChangeSetNewRowSchema,
  ExportFileStreamSchema,
  refineCatalogSearch,
} from "./products.contract";
import {
  ExportChangeSetReviewLinesInputSchema,
  ListChangeSetReviewLinesInputSchema,
  ListChangeSetReviewLinesResponseSchema,
} from "./change-set-review.contract";

const UUIDSchema = z.uuid();

export const MetadataChangeSetSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  description: z.string().nullable(),
  status: z.nativeEnum(MetadataChangeSetStatus),
  createdByUserId: UUIDSchema.nullable(),
  assigneeId: UUIDSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const ListMetadataChangeSetsResponseSchema = z.object({
  changeSets: z.array(MetadataChangeSetSchema),
});

export const GetMetadataChangeSetInputSchema = z.object({
  id: UUIDSchema,
});

// The change set's metadata only. Its contents are browsed through the
// changeSetId-scoped catalog endpoints (products.listCatalog,
// products.listAs400Export, raw-data records), which carry the staged-change
// overlays.
export const GetMetadataChangeSetResponseSchema = z.object({
  changeSet: MetadataChangeSetSchema.nullable(),
});

// Name/description shared by both staging entry points.
const StageChangeSetBaseSchema = z.object({
  name: z.string().trim().min(1).max(255),
  description: z.string().trim().max(2000).optional(),
});

// The most product keys one `stageSelected` call accepts.
export const MAX_STAGE_SELECTED_PRODUCT_KEYS = 50000;

// Stage an explicit selection of catalog products as a new change set. The
// client only sends product keys, so a selection survives paging.
export const StageSelectedChangesInputSchema = StageChangeSetBaseSchema.extend({
  productKeys: z
    .array(z.string().min(1))
    .min(1)
    .max(MAX_STAGE_SELECTED_PRODUCT_KEYS),
});

// Stage every product matching the current catalog search + filters (resolved
// server-side, streamed in batches). Unbounded — the client warns above the
// staging threshold using the catalog's total count.
export const StageAllChangesInputSchema = StageChangeSetBaseSchema.extend({
  search: z.string().trim().optional(),
  searchMode: z.enum(["text", "regex"]).default("text"),
  advancedFilters: z.array(CatalogAdvancedFilterSchema).max(20).optional(),
  // Part of "matching" for the same reason: the same filters read row-by-row
  // and product-wide select different products.
  ...catalogFilterMatchField,
  // The catalog's key scope (Source / Location) is part of "matching": staging
  // without it would stage records from divisions and locations the reader had
  // filtered out.
  ...CatalogRecordKeyScopeSchema.shape,
}).superRefine(refineCatalogSearch);

// Stage every product of an org-shared item list as a new change set. The
// list's product keys are streamed server-side — they never transit the client.
export const StageFromItemListInputSchema = StageChangeSetBaseSchema.extend({
  itemListId: UUIDSchema,
});

// Copy a review into a new draft: its products, staged edits and new rows.
export const DuplicateMetadataChangeSetInputSchema =
  StageChangeSetBaseSchema.extend({ id: UUIDSchema });

export const StageChangesResponseSchema = z.object({
  changeSetId: UUIDSchema,
  // Number of products staged into the new change set.
  stagedCount: z.number().int(),
});

export const UpdateMetadataChangeSetInputSchema = z
  .object({
    id: UUIDSchema,
    name: z.string().trim().min(1).max(255).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    status: z.nativeEnum(MetadataChangeSetStatus).optional(),
    // null clears the assignment; a UUID assigns to that user.
    assigneeId: UUIDSchema.nullable().optional(),
  })
  .refine(
    (input) =>
      input.name !== undefined ||
      input.description !== undefined ||
      input.status !== undefined ||
      input.assigneeId !== undefined,
    { message: "At least one field to update must be provided" },
  );

export const UpdateMetadataChangeSetResponseSchema = z.object({
  changeSet: MetadataChangeSetSchema,
});

// The changed cells shared by both staged-edit shapes: `values` keyed by
// canonical field code (a null value clears the field), and `originalValues`
// snapshotting the live cell values at edit time — the drift baseline stored
// with the staged change.
//
// Only `values` is held to ASCII: it is what the AS/400 upload file carries. An
// `originalValues` entry is a copy of what the ingest mirror already holds, so
// refusing one would block a legitimate edit over a character the review didn't
// author and can't reach.
const stagedEditCells = {
  values: z.record(z.string().min(1), As400CellValueSchema.nullable()),
  originalValues: z.record(z.string().min(1), z.string().nullable()),
};
const hasChangedField = (edit: { values: Record<string, string | null> }) =>
  Object.keys(edit.values).length > 0;
const AT_LEAST_ONE_FIELD = {
  message: "An edit must change at least one field",
};

// One edited export row: the ingest source row(s) it was assembled from, plus
// the changed cells.
export const StagedExportRowEditSchema = z
  .object({
    sources: z.array(As400ExportRowSourceSchema).min(1).max(8),
    ...stagedEditCells,
  })
  .refine(hasChangedField, AT_LEAST_ONE_FIELD);

// Stage inline edits made in the review page's Export tab. The group +
// dataSourceIds identify the flattened view the rows came from, so the server
// can attribute each edited column to the ingest table that owns it. Several
// ids when the view combines divisions — each edited row's own sources pick out
// the division it belongs to, so attribution stays correct.
export const StageDataLinkEditsInputSchema = z.object({
  changeSetId: UUIDSchema,
  group: As400ExportGroupSchema,
  dataSourceIds: z.array(UUIDSchema).min(1).max(50),
  edits: z.array(StagedExportRowEditSchema).min(1).max(500),
});

// One edited export row's staged state after the save, in the shape the
// export view's rows carry it: the merged staged overlay (null once nothing is
// staged on the row) and the drift baselines of its staged fields. The client
// patches these onto the rows it already holds rather than refetching the
// page (GEN-2946).
export const StagedExportRowStateSchema = As400ExportRowSchema.pick({
  key: true,
  sources: true,
  stagedValues: true,
  stagedOriginalValues: true,
});

export const StageDataLinkEditsResponseSchema = z.object({
  // Number of ingest rows whose staged change was written (or removed, when
  // every edited field reverted to its live value).
  savedRowCount: z.number().int(),
  // The edited rows' staged state, one per input edit, in input order.
  rows: z.array(StagedExportRowStateSchema),
});

// One edited raw record from the Search-tab AS/400 view: its ingest row
// identity (dataSourceLinkId + dataId) and the changed cells. Unlike the
// worksheet edits above, each row IS a single ingest record, so no column→table
// attribution is needed — the field goes straight to that record.
export const StagedRawRowEditSchema = z
  .object({
    dataSourceLinkId: UUIDSchema,
    dataId: UUIDSchema,
    ...stagedEditCells,
  })
  .refine(hasChangedField, AT_LEAST_ONE_FIELD);

// Stage inline edits made in the review page's Search-tab AS/400 view, where
// each edited row maps directly to one ingest record.
export const StageRawDataEditsInputSchema = z.object({
  changeSetId: UUIDSchema,
  edits: z.array(StagedRawRowEditSchema).min(1).max(500),
});

export const StageRawDataEditsResponseSchema = z.object({
  savedRowCount: z.number().int(),
});

// ─── New rows ─────────────────────────────────────────────────────────────────
// Net-new worksheet rows authored on a review: products the AS/400 doesn't
// have yet, staged per export worksheet + division so the upload file can
// create them. Values are keyed by the worksheet column's edit key and
// validated server-side; the group's item-number column is required
// (workbook-normalized: upper case, no spaces) and unique within the review.

export const AddChangeSetNewRowInputSchema = z.object({
  changeSetId: UUIDSchema,
  group: As400ExportGroupSchema,
  dataSourceId: UUIDSchema,
  // For a per-branch division (RD), the location to add it at; null = the
  // whole division. See `AS400_BRANCH_SPLIT_LOCATIONS_BY_DIVISION`.
  branch: As400BranchCodeSchema.nullish(),
  values: z.record(z.string().min(1), As400CellValueSchema.nullable()),
});

export const AddChangeSetNewRowResponseSchema = z.object({
  row: ChangeSetNewRowSchema,
});

// Merge cell edits into existing new rows (a null value unsets the cell; the
// item-number column can change but never blank).
export const UpdateChangeSetNewRowsInputSchema = z.object({
  changeSetId: UUIDSchema,
  edits: z
    .array(
      z
        .object({
          id: UUIDSchema,
          values: z.record(z.string().min(1), As400CellValueSchema.nullable()),
        })
        .refine(hasChangedField, AT_LEAST_ONE_FIELD),
    )
    .min(1)
    .max(500),
});

export const UpdateChangeSetNewRowsResponseSchema = z.object({
  savedRowCount: z.number().int(),
});

export const DeleteChangeSetNewRowInputSchema = z.object({
  changeSetId: UUIDSchema,
  id: UUIDSchema,
});

export const DeleteChangeSetNewRowResponseSchema = z.object({
  success: z.boolean(),
});

// One change-set-level history event: what happened to the change set (not to
// an individual staged row), who did it, and when. `metadata` carries the
// action's detail, e.g. { from, to } for status changes or { savedRowCount }
// for staged-edit saves.
export const MetadataChangeSetEventSchema = z.object({
  id: UUIDSchema,
  action: z.nativeEnum(MetadataChangeSetEventAction),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  actorUserId: UUIDSchema.nullable(),
  // Display name of the acting user; null for system actions or deleted users.
  actorName: z.string().nullable(),
  createdAt: z.string(),
});

export const ListMetadataChangeSetEventsInputSchema = z.object({
  changeSetId: UUIDSchema,
});

export const ListMetadataChangeSetEventsResponseSchema = z.object({
  // Newest first.
  events: z.array(MetadataChangeSetEventSchema),
});

// One saved state of a change set's staged edits. The snapshot itself stays
// server-side — the list only describes each version well enough to pick one.
export const MetadataChangeSetVersionSchema = z.object({
  id: UUIDSchema,
  // 1-based, per change set.
  versionNumber: z.number().int().positive(),
  // Ingest rows carrying a staged edit in this version, and the changed fields
  // across them.
  rowCount: z.number().int().nonnegative(),
  fieldCount: z.number().int().nonnegative(),
  source: z.nativeEnum(MetadataChangeSetVersionSource),
  // Whether this version is the change set's live staged state.
  isCurrent: z.boolean(),
  // Display name of the version's author; null for system or deleted users.
  createdByName: z.string().nullable(),
  createdAt: z.string(),
});

export const ListMetadataChangeSetVersionsInputSchema = z.object({
  changeSetId: UUIDSchema,
});

export const ListMetadataChangeSetVersionsResponseSchema = z.object({
  // Newest first.
  versions: z.array(MetadataChangeSetVersionSchema),
});

export const RestoreMetadataChangeSetVersionInputSchema = z.object({
  changeSetId: UUIDSchema,
  versionId: UUIDSchema,
});

export const RestoreMetadataChangeSetVersionResponseSchema = z.object({
  versionId: UUIDSchema,
  versionNumber: z.number().int().positive(),
});

export const DeleteMetadataChangeSetInputSchema = z.object({ id: UUIDSchema });

export const DeleteMetadataChangeSetResponseSchema = z.object({
  success: z.boolean(),
});

export const metadataChangeSetsContracts = {
  list: oc
    .input(z.object({}))
    .output(ListMetadataChangeSetsResponseSchema)
    .route({
      method: "GET",
      summary: "List product catalog change sets",
      description:
        "Returns the organization's product metadata change sets (catalog edit reviews) with name, description, draft or published status, creator and assignee.",
    }),
  get: oc
    .input(GetMetadataChangeSetInputSchema)
    .output(GetMetadataChangeSetResponseSchema)
    .route({
      method: "GET",
      summary: "Get a product catalog change set",
      description:
        "Returns one product metadata change set's name, description, status and assignee; its staged product edits are browsed through the catalog views.",
    }),
  stageSelected: oc
    .input(StageSelectedChangesInputSchema)
    .output(StageChangesResponseSchema)
    .route({
      method: "POST",
      summary: "Create a change set from selected products",
      description:
        "Creates a new draft product metadata change set staging the selected catalog products (up to 50,000 SKUs); returns its id and staged count.",
    }),
  stageAll: oc
    .input(StageAllChangesInputSchema)
    .output(StageChangesResponseSchema)
    .route({
      method: "POST",
      summary: "Create a change set from catalog filters",
      description:
        "Creates a new draft product metadata change set staging every catalog product matching the current search, filters, source and location; returns its id and count.",
    }),
  stageFromItemList: oc
    .input(StageFromItemListInputSchema)
    .output(StageChangesResponseSchema)
    .route({
      method: "POST",
      summary: "Create a change set from an item list",
      description:
        "Creates a new draft product metadata change set staging every product in a shared item list; returns its id and staged count.",
    }),
  duplicate: oc
    .input(DuplicateMetadataChangeSetInputSchema)
    .output(StageChangesResponseSchema)
    .route({
      method: "POST",
      summary: "Duplicate a product catalog change set",
      description:
        "Copies a change set's products, staged field edits and new rows into a new draft change set with a new name.",
    }),
  stageDataLinkEdits: oc
    .input(StageDataLinkEditsInputSchema)
    .output(StageDataLinkEditsResponseSchema)
    .route({
      method: "POST",
      summary: "Save worksheet edits to a change set",
      description:
        "Saves inline cell edits from a change set's ERP upload worksheet view as staged changes on the underlying records; edits reverted to live values are removed.",
    }),
  stageRawDataEdits: oc
    .input(StageRawDataEditsInputSchema)
    .output(StageRawDataEditsResponseSchema)
    .route({
      method: "POST",
      summary: "Save raw record edits to a change set",
      description:
        "Saves inline cell edits to individual raw ERP catalog records as staged changes on a change set, up to 500 rows per call.",
    }),
  addNewRow: oc
    .input(AddChangeSetNewRowInputSchema)
    .output(AddChangeSetNewRowResponseSchema)
    .route({
      method: "POST",
      summary: "Add a new product row to a change set",
      description:
        "Adds a net-new worksheet row (a product the ERP does not have yet) to a change set for one division and optional branch; the item number is required and unique.",
    }),
  updateNewRows: oc
    .input(UpdateChangeSetNewRowsInputSchema)
    .output(UpdateChangeSetNewRowsResponseSchema)
    .route({
      method: "POST",
      summary: "Edit new product rows in a change set",
      description:
        "Merges cell edits into a change set's net-new worksheet rows; a null value clears a cell, and the item number cannot be blanked.",
    }),
  deleteNewRow: oc
    .input(DeleteChangeSetNewRowInputSchema)
    .output(DeleteChangeSetNewRowResponseSchema)
    .route({
      method: "POST",
      summary: "Remove a new product row from a change set",
      description:
        "Deletes one net-new worksheet row from a product metadata change set.",
    }),
  listEvents: oc
    .input(ListMetadataChangeSetEventsInputSchema)
    .output(ListMetadataChangeSetEventsResponseSchema)
    .route({
      method: "GET",
      summary: "List a change set's history events",
      description:
        "Returns a change set's history (creation, detail edits, status and assignee changes, staged-edit saves, version restores), newest first, with the acting user.",
    }),
  listReviewLines: oc
    .input(ListChangeSetReviewLinesInputSchema)
    .output(ListChangeSetReviewLinesResponseSchema)
    .route({
      method: "GET",
      summary: "List a change set's changed cells for review",
      description:
        "Returns every cell a change set changes or adds, live AS/400 value to staged value, grouped by item number and company/division/branch and paged by group; filterable by worksheet and column.",
    }),
  exportReviewLines: oc
    .input(ExportChangeSetReviewLinesInputSchema)
    .output(ExportFileStreamSchema)
    .route({
      method: "GET",
      summary: "Download a change set's review list as Excel",
      description:
        "Downloads every cell a change set changes, adds or has a comment on, old and new value with the comment threads, as an Excel workbook; filterable by worksheet and column. Very large lists come as a zip of workbooks.",
    }),
  listVersions: oc
    .input(ListMetadataChangeSetVersionsInputSchema)
    .output(ListMetadataChangeSetVersionsResponseSchema)
    .route({
      method: "GET",
      summary: "List a change set's saved versions",
      description:
        "Returns the saved versions of a change set's staged edits, newest first.",
    }),
  restoreVersion: oc
    .input(RestoreMetadataChangeSetVersionInputSchema)
    .output(RestoreMetadataChangeSetVersionResponseSchema)
    .route({
      method: "POST",
      summary: "Restore a previous change set version",
      description:
        "Restores a change set's staged edits to an earlier saved version and makes it current.",
    }),
  update: oc
    .input(UpdateMetadataChangeSetInputSchema)
    .output(UpdateMetadataChangeSetResponseSchema)
    .route({
      method: "PATCH",
      summary: "Update a product catalog change set",
      description:
        "Changes a change set's name, description, draft or published status, or assignee (null unassigns); the change is recorded in its history.",
    }),
  delete: oc
    .input(DeleteMetadataChangeSetInputSchema)
    .output(DeleteMetadataChangeSetResponseSchema)
    .route({
      method: "DELETE",
      summary: "Delete a product catalog change set",
      description:
        "Deletes a product metadata change set along with all of its staged changes.",
    }),
} as const;

export type MetadataChangeSetDTO = z.infer<typeof MetadataChangeSetSchema>;
export type ListMetadataChangeSetsResponse = z.infer<
  typeof ListMetadataChangeSetsResponseSchema
>;
export type GetMetadataChangeSetResponse = z.infer<
  typeof GetMetadataChangeSetResponseSchema
>;
export type StageSelectedChangesInput = z.infer<
  typeof StageSelectedChangesInputSchema
>;
export type StageAllChangesInput = z.infer<typeof StageAllChangesInputSchema>;
export type StageFromItemListInput = z.infer<
  typeof StageFromItemListInputSchema
>;
export type DuplicateMetadataChangeSetInput = z.infer<
  typeof DuplicateMetadataChangeSetInputSchema
>;
export type StageChangesResponse = z.infer<typeof StageChangesResponseSchema>;
export type StagedExportRowEdit = z.infer<typeof StagedExportRowEditSchema>;
export type StageDataLinkEditsInput = z.infer<
  typeof StageDataLinkEditsInputSchema
>;
export type StageDataLinkEditsResponse = z.infer<
  typeof StageDataLinkEditsResponseSchema
>;
export type StagedExportRowState = z.infer<typeof StagedExportRowStateSchema>;
export type StagedRawRowEdit = z.infer<typeof StagedRawRowEditSchema>;
export type StageRawDataEditsInput = z.infer<
  typeof StageRawDataEditsInputSchema
>;
export type StageRawDataEditsResponse = z.infer<
  typeof StageRawDataEditsResponseSchema
>;
export type MetadataChangeSetEventDTO = z.infer<
  typeof MetadataChangeSetEventSchema
>;
export type ListMetadataChangeSetEventsResponse = z.infer<
  typeof ListMetadataChangeSetEventsResponseSchema
>;
export type MetadataChangeSetVersionDTO = z.infer<
  typeof MetadataChangeSetVersionSchema
>;
export type ListMetadataChangeSetVersionsResponse = z.infer<
  typeof ListMetadataChangeSetVersionsResponseSchema
>;
export type RestoreMetadataChangeSetVersionInput = z.infer<
  typeof RestoreMetadataChangeSetVersionInputSchema
>;
export type RestoreMetadataChangeSetVersionResponse = z.infer<
  typeof RestoreMetadataChangeSetVersionResponseSchema
>;
export type UpdateMetadataChangeSetInput = z.infer<
  typeof UpdateMetadataChangeSetInputSchema
>;
export type UpdateMetadataChangeSetResponse = z.infer<
  typeof UpdateMetadataChangeSetResponseSchema
>;
export type DeleteMetadataChangeSetResponse = z.infer<
  typeof DeleteMetadataChangeSetResponseSchema
>;
export type AddChangeSetNewRowInput = z.infer<
  typeof AddChangeSetNewRowInputSchema
>;
export type AddChangeSetNewRowResponse = z.infer<
  typeof AddChangeSetNewRowResponseSchema
>;
export type UpdateChangeSetNewRowsInput = z.infer<
  typeof UpdateChangeSetNewRowsInputSchema
>;
export type UpdateChangeSetNewRowsResponse = z.infer<
  typeof UpdateChangeSetNewRowsResponseSchema
>;
export type DeleteChangeSetNewRowInput = z.infer<
  typeof DeleteChangeSetNewRowInputSchema
>;
export type DeleteChangeSetNewRowResponse = z.infer<
  typeof DeleteChangeSetNewRowResponseSchema
>;
